import { useState, useEffect, useRef, useCallback } from 'react';
import { AxiosError } from 'axios';
import mainService from '../../../services/Main';
import { RequestConfig } from '../../../services/Main/types.Component';
import { ActionServerResponse } from '../../../services/Main/types';
import { useFormatMessage } from '../../../locale';

export interface BatchProgressResponse {
  status: 'pending' | 'fulfilled';
  progressCount: number;
  totalCount: number;
  failureCount: number | null;
  result: null | {
    html: string;
  };
}

const MAX_RETRIES = 10; // Максимум попыток для любых ошибок
const NORMAL_POLLING_INTERVAL = 500; // Обычный интервал polling
const RETRY_INTERVAL_ON_ERROR = 1000; // Интервал при isSuccess: false
const RETRY_INTERVAL_ON_NETWORK_ERROR = 2000; // Интервал при сетевой ошибке

export const useBatchProgress = (
  requestConfig?: RequestConfig | null,
  onFinish?: () => void
) => {
  const formatMessage = useFormatMessage();
  const [progress, setProgress] = useState<BatchProgressResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isPollingRef = useRef(false);
  const totalFailuresRef = useRef(0); // Общий счетчик неудач для всех типов ошибок

  const handleFinish = useCallback(() => {
    // Сбрасываем состояние ошибки при завершении
    setError(null);
    onFinish?.();
  }, [onFinish]);

  const handleError = useCallback((errorMessage: string) => {
    setError(errorMessage);
  }, []);

  const onFinishRef = useRef(handleFinish);
  const onErrorRef = useRef(handleError);

  useEffect(() => {
    onFinishRef.current = handleFinish;
    onErrorRef.current = handleError;
  }, [handleFinish, handleError]);

  const stopPolling = useCallback(() => {
    isPollingRef.current = false;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsLoading(false);
  }, []);

  const startPolling = useCallback(() => {
    if (!requestConfig || isPollingRef.current) return;

    isPollingRef.current = true;
    setIsLoading(true);
    setError(null);
    setProgress(null);
    totalFailuresRef.current = 0;

    // Функция для обработки ошибок бэкенда (isSuccess: false или HTTP ошибки)
    const handleBackendError = () => {
      totalFailuresRef.current += 1;

      if (totalFailuresRef.current >= MAX_RETRIES) {
        const errorMessage = formatMessage('batchActionError');

        stopPolling();
        onErrorRef.current(errorMessage);
        // Прекращаем polling
        return false;
      }

      // Продолжаем polling при ошибках бэкенда
      if (isPollingRef.current) {
        timeoutRef.current = setTimeout(poll, RETRY_INTERVAL_ON_ERROR);
      }

      // Продолжаем polling
      return true;
    };

    const poll = async () => {
      if (!isPollingRef.current) return;

      try {
        const response = await mainService.makeRequest<
          ActionServerResponse<BatchProgressResponse>
        >(requestConfig);

        if (!isPollingRef.current) return;

        if (!response.isSuccess) {
          handleBackendError();
          return;
        }

        // При успешном ответе сбрасываем счетчики
        totalFailuresRef.current = 0;

        setProgress(response.payload);

        if (response.payload.status === 'fulfilled') {
          stopPolling();
          onFinishRef.current();
          return;
        }

        if (isPollingRef.current) {
          timeoutRef.current = setTimeout(poll, NORMAL_POLLING_INTERVAL);
        }
      } catch (err) {
        // Различаем сетевые ошибки от ошибок сервера
        // Если у ошибки есть response - это HTTP ошибка, иначе - сетевая
        const isNetworkError = !(err as AxiosError)?.response;

        if (isNetworkError) {
          // Сетевая ошибка (нет интернета, таймаут, отмененный запрос)
          totalFailuresRef.current += 1;

          if (totalFailuresRef.current >= MAX_RETRIES) {
            stopPolling();
            onErrorRef.current(formatMessage('networkConnectionError'));
            return;
          }

          // При сетевой ошибке также увеличиваем общий счетчик неудач
          if (isPollingRef.current) {
            timeoutRef.current = setTimeout(
              poll,
              RETRY_INTERVAL_ON_NETWORK_ERROR
            );
          }
        } else {
          // HTTP ошибка (404, 500, etc.) - обрабатываем как isSuccess: false
          handleBackendError();
        }
      }
    };

    poll();
    // eslint-disable-next-line
  }, [requestConfig, stopPolling]);

  useEffect(() => {
    if (requestConfig) {
      startPolling();
    }

    return () => {
      stopPolling();
    };
  }, [requestConfig, startPolling, stopPolling]);

  const getProgressPercentage = () => {
    if (!progress || progress.totalCount === 0) return 0;
    return Math.round((progress.progressCount / progress.totalCount) * 100);
  };

  const getProgressText = () => {
    if (!progress) return '\u00A0';

    return `${progress.progressCount} / ${progress.totalCount}`;
  };

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    progress,
    isLoading,
    error,
    getProgressPercentage,
    getProgressText,
    stopPolling,
    setProgress,
    clearError,
  };
};
